import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import keyboardKey from 'keyboard-key';
import get from 'lodash/get';
import { Grid, Button, Checkbox, Dropdown, Icon } from '../../components';

import { stateOptions, studentBodySizesList } from './filtersOptions';
import messages from './messages';
import ZipCode from './zipCode';
import CollegeNameInput from './collegeInput';
import Timeframe from './timeframeInput';

import './styles.css';

class Filters extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    modal: PropTypes.bool,
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
    majors: PropTypes.array,
    filters: PropTypes.object,
    filterEvents: PropTypes.func.isRequired,
    viewAllEvents: PropTypes.bool,
    filteredByRecommended: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      hideDayPickers: false,
      filteredByRecommended: false,
      filteredByVirtualEvent: false,
    };
  }

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.viewAllEvents === true) {
      return { filteredByRecommended: false, filteredByVirtualEvent: false };
    } else return null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.filteredByRecommended !== this.props.filteredByRecommended) {
      this.setState({ filteredByRecommended: this.props.filteredByRecommended });
    }
  }

  onChangeRecommended = () => {
    const { filteredByRecommended } = this.state;
    this.setState((prevState) => ({ filteredByRecommended: !prevState.filteredByRecommended }));
    this.props.filterByRecommended(!filteredByRecommended, false);
  };

  onChangeVirtualEvents = () => {
    this.setState(
      (prevState) => ({ filteredByVirtualEvent: !prevState.filteredByVirtualEvent }),
      () => {
        const { filteredByVirtualEvent } = this.state;
        this.props.filterByVirtualEvents(filteredByVirtualEvent, false);
        if (filteredByVirtualEvent) this.props.filterEvents('location.distance', {}, 'distance');
      },
    );
  };

  clearFilters = () => {
    // Reset input values
    setTimeout(() => document.activeElement.blur());
    this.props.clearFilters();
    this.setState({ hideDayPickers: true }, () =>
      this.setState({
        hideDayPickers: false,
        filteredByVirtualEvent: false,
        filteredByRecommended: false,
      }),
    );
  };

  onChange = (e, { name, value, id }) => {
    this.props.filterEvents(name, value, id);
  };

  onChangeMajors = (e, { name, value, id }) => {
    if (value.length <= 3) this.onChange(e, { name, value, id });
  };

  onCollegeSizeKeyDown = (event) => {
    const sizes = get(this.props, 'filters.sizes', []);
    const key = keyboardKey.getCode(event);
    if (key !== keyboardKey.Delete && key !== keyboardKey.Backspace) return;
    this.props.filterEvents('sizes', sizes.slice(0, sizes.length - 1), 'sizes');
  };

  renderFilterOptions = () => {
    const { intl } = this.props;
    const { filteredByRecommended, filteredByVirtualEvent } = this.state;
    return (
      <>
        <div className="choice small">
          <strong>
            <FormattedMessage {...messages.recommended} />
          </strong>
        </div>
        <div className="choice college-event-filters-choice-closer">
          <Checkbox
            name="recommended"
            aria-label={intl.formatMessage(messages.recommendedevents)}
            label={intl.formatMessage(messages.recommendedevents)}
            checked={filteredByRecommended}
            onClick={this.onChangeRecommended}
            data-cy="filter-by-recommended"
          />
        </div>
        <div className="choice">
          <Checkbox
            name="virtual"
            aria-label={intl.formatMessage(messages.virtualevents)}
            label={intl.formatMessage(messages.virtualevents)}
            checked={filteredByVirtualEvent}
            onClick={this.onChangeVirtualEvents}
            data-cy="filter-by-virtual-events"
          />
        </div>
      </>
    );
  };

  renderFilterZipCode = () => {
    const { distance } = this.props.filters;
    const { filteredByVirtualEvent } = this.state;
    return <ZipCode virtual={filteredByVirtualEvent} onChange={this.onChange} distance={distance} />;
  };

  renderFilterDateRange = () => {
    const { filterEvents } = this.props;
    const { dateFrom, dateTo } = this.props.filters;
    return <Timeframe hide={this.state.hideDayPickers} timeframe={{ dateFrom, dateTo }} filterEvents={filterEvents} />;
  };

  renderFilterCollegeStates = () => {
    const { intl } = this.props;
    const { states } = this.props.filters;
    return (
      <div className="college-size">
        <strong>
          <FormattedMessage {...messages.collegelocated} />
        </strong>
        <Dropdown
          name="location.states"
          id="states"
          className="block-field no-margin college-events-filters-states"
          placeholder={intl.formatMessage(messages.location)}
          data-cy="filter-location"
          multiple
          fluid
          selection
          search
          closeOnChange
          value={states || []}
          options={stateOptions}
          onChange={this.onChange}
        />
      </div>
    );
  };

  renderFilterCollegeSizes = () => {
    const { intl } = this.props;
    const { sizes } = this.props.filters;
    return (
      <div data-cy="sizes-container" onKeyDown={this.onCollegeSizeKeyDown}>
        <strong>
          <FormattedMessage {...messages.collegesize} />
        </strong>
        <div>
          <Dropdown
            name="sizes"
            id="sizes"
            className="block-field no-margin"
            placeholder={intl.formatMessage(messages.size)}
            data-cy="filter-size"
            multiple
            fluid
            selection
            closeOnChange
            value={sizes || []}
            options={studentBodySizesList}
            onChange={this.onChange}
          />
        </div>
      </div>
    );
  };

  renderFilterHostedBy = () => {
    const { name } = this.props.filters;
    return <CollegeNameInput onChange={this.onChange} collegeName={name} />;
  };

  renderFilterMajors = () => {
    const { intl } = this.props;
    const { majors } = this.props.filters;
    const majorsOptions = this.props.majors || [];
    return (
      <div className="majors-container">
        <strong className="majors-hint">
          <FormattedMessage {...messages.collegemajors} />
        </strong>
        <Dropdown
          placeholder={intl.formatMessage(messages.majors)}
          icon="search"
          multiple
          search
          selection
          data-cy="filter-majors"
          name="majors"
          id="majors"
          className="block-field low-margin"
          value={majors || []}
          closeOnChange
          options={majorsOptions.map((major) => ({ key: major, text: major, value: major }))}
          onChange={this.onChangeMajors}
        />
      </div>
    );
  };

  renderFilterMobileButton = () => {
    const { modal, openModal } = this.props;
    if (modal) return null;
    return (
      <Grid.Column width={3} only={'mobile'} textAlign="right">
        <div className="show-filters">
          <Button size="small" basic onClick={openModal} data-cy="mobile-open-modal">
            <FormattedMessage {...messages.showfilters} />
          </Button>
        </div>
      </Grid.Column>
    );
  };

  renderFilterMobileHeader = () => {
    const { modal, closeModal } = this.props;
    if (!modal) return null;
    return (
      <Grid.Column className="mobile-events-column">
        <div className="mobile-events">
          <Button basic onClick={closeModal} data-cy="mobile-filters-close">
            <Icon name="times" />
            <FormattedMessage {...messages.close} />
          </Button>
          <Button basic onClick={this.clearFilters}>
            <FormattedMessage {...messages.reset} />
          </Button>
        </div>
      </Grid.Column>
    );
  };

  renderFilterMobileFooter = () => {
    const { modal, closeModal } = this.props;
    if (!modal) return null;
    return (
      <Grid.Column>
        <div className="done">
          <Button primary onClick={closeModal} data-cy="mobile-filters-done">
            <FormattedMessage {...messages.done} />
          </Button>
        </div>
      </Grid.Column>
    );
  };

  render() {
    const { modal } = this.props;
    return (
      <div className="college-events-filters">
        <Grid stackable>
          <Grid.Row>
            {this.renderFilterMobileHeader()}

            <Grid.Column style={{ flex: 20 }} only={'large-screen'} />

            <Grid.Column style={{ flex: 18 }}>
              {this.renderFilterOptions()}
              <div className="clear-filters hide">
                <Button size="small" basic onClick={this.clearFilters} data-cy="clear-filters">
                  <FormattedMessage {...messages.clearfilters} />
                </Button>
              </div>
            </Grid.Column>

            <Grid.Column style={{ flex: 20 }} only={modal ? 'mobile' : 'tablet computer'}>
              {this.renderFilterZipCode()}
              {this.renderFilterDateRange()}
            </Grid.Column>

            <Grid.Column style={{ flex: 5 }} only={'widescreen'} />

            <Grid.Column style={{ flex: 25 }} only={modal ? 'mobile' : 'tablet computer'}>
              {this.renderFilterCollegeStates()}
              {this.renderFilterCollegeSizes()}
            </Grid.Column>

            <Grid.Column style={{ flex: 7 }} only={'widescreen'} />

            <Grid.Column style={{ flex: 25 }} only={modal ? 'mobile' : 'tablet computer'}>
              {this.renderFilterHostedBy()}
              {this.renderFilterMajors()}
            </Grid.Column>

            <Grid.Column style={{ flex: 20 }} only={'large-screen'} />

            {this.renderFilterMobileButton()}

            {this.renderFilterMobileFooter()}
          </Grid.Row>
        </Grid>
      </div>
    );
  }
}
export default injectIntl(Filters);
